/**
 * The shared browser-auth API (`/api/v2/auth`).
 *
 * A separate client from `lib/api.ts` for one reason that is not organisational:
 * this sub-app answers errors as RFC 9457 **Problem Details**
 * (`{type, title, status, code, detail}`), while the compliance API answers with
 * its flat `{code, message, request_id}` envelope. Parsing one with the other's
 * schema turns every real error into MALFORMED_ERROR and loses the code the UI
 * branches on.
 *
 * Both shapes are normalised to `ApiError` — through the same
 * `errorEnvelopeFrom` the compliance client uses, so callers upstream of this
 * module see exactly one error type regardless of which sub-app refused them,
 * and neither client can drift into rejecting the other's shape.
 */

import { ApiError } from "@compliance/domain";
import type { z } from "zod";
import { AUTH_BASE_URL } from "../lib/api";
import { errorEnvelopeFrom } from "../lib/errors";
import { loginResponseSchema, sessionResponseSchema } from "../lib/schemas";
import type { LoginOutcome, NewPasswordChallenge, Session } from "./types";

/**
 * Attributes the API refuses in a challenge response, because the identity
 * resolver selects the canonical account by them. Sending either is a 422, so
 * they are dropped before the request rather than surfaced as a failure.
 */
const PROTECTED_ATTRIBUTES = new Set(["email", "email_verified"]);

/** Drop the attributes the API will not accept, comparing case-insensitively. */
export function usableAttributes(names: readonly string[]): string[] {
  return names.filter((name) => !PROTECTED_ATTRIBUTES.has(name.toLowerCase()));
}

/** Turn a Problem Details body — or anything else — into an ApiError. */
async function authError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get("X-Request-Id") ?? "unknown";
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiError(response.status, {
      code: response.status === 0 ? "NETWORK_ERROR" : "MALFORMED_ERROR",
      message: `Request failed: ${response.status} ${response.statusText}`,
      request_id: requestId,
    });
  }
  // Shared with `lib/api.ts` rather than parsed here: this sub-app answers in
  // Problem Details and the compliance API in its flat envelope, but which one
  // arrives is not reliably a property of which client asked — the API edge
  // writes Problem Details for anything neither app reached. One normaliser
  // means neither client can regress into reporting the other's shape as
  // MALFORMED_ERROR.
  const envelope = errorEnvelopeFrom(body, requestId);
  if (envelope === null) {
    return new ApiError(response.status, {
      code: "MALFORMED_ERROR",
      message: `Server returned ${response.status} but the problem body was not parseable`,
      request_id: requestId,
    });
  }
  return new ApiError(response.status, envelope);
}

// Generic over the schema, returning its *output* type. `z.ZodType<T>` would
// pin T to the input side, and these schemas transform (`.nullish()`,
// `.default([])`), so input and output genuinely differ.
/**
 * How long any auth call may hang before it is abandoned.
 *
 * Unbounded, these never settle when the origin accepts a connection and then
 * stops talking — and `bootstrapSession()` is awaited before `loading` clears,
 * so the whole SPA sits on its full-screen loader forever with no way out. The
 * retry loop makes it worse rather than better: its first attempt hangs and the
 * other two never run. Ten seconds is longer than any of these calls should
 * take and short enough that the unavailable-session UI takes over while someone
 * is still watching.
 */
const AUTH_TIMEOUT_MS = 10_000;

/** Did the API refuse this specifically because the CSRF token was stale? */
async function isStaleCsrf(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  return response
    .clone()
    .json()
    .then((body: { code?: string }) => body.code === "CSRF_FAILED")
    .catch(() => false);
}

async function authFetch<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  init: RequestInit & { csrfToken?: string } = {},
): Promise<z.output<S>> {
  const { csrfToken, ...rest } = init;
  const send = (token: string | undefined) => {
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Request-Id": crypto.randomUUID(),
    });
    if (token) headers.set("X-CSRF-Token", token);
    return fetch(`${AUTH_BASE_URL}${path}`, {
      ...rest,
      headers,
      // The whole point: the session travels as cookies, and a cross-origin
      // fetch omits them unless asked.
      credentials: "include",
      signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
    });
  };

  let response = await send(csrfToken);

  // Recover a token another tab's refresh rotated. Handled here rather than at
  // each caller because every CSRF-gated call on this sub-app can meet it, and
  // patching them one at a time is what left `/refresh` exposed: a stale token
  // there answers 403, which reads as a settled refusal and ends a session whose
  // refresh cookie is perfectly good.
  //
  // `/session` is a GET and needs no token, so this cannot recurse.
  if (csrfToken && path !== "/session" && (await isStaleCsrf(response))) {
    const current = await bootstrapSession();
    response = await send(current.csrfToken);
  }

  if (!response.ok) throw await authError(response);

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError(0, {
      code: "CONTRACT_VIOLATION",
      message: `Session response failed schema validation: ${parsed.error.message}`,
      request_id: response.headers.get("X-Request-Id") ?? "unknown",
      details: { issues: parsed.error.issues },
    });
  }
  return parsed.data;
}

/**
 * Ask the API what session we hold. Answers 200 for an anonymous caller, so
 * this is safe to call unconditionally on boot — and it is the **only** source
 * of the CSRF token, which is why even the anonymous answer carries one.
 */
export function bootstrapSession(): Promise<Session> {
  return authFetch("/session", sessionResponseSchema, { method: "GET" });
}

/**
 * Offer an email and password. CSRF-gated.
 *
 * Two possible successes, which is why this does not simply return a `Session`:
 * an established session, or a `NEW_PASSWORD_REQUIRED` challenge that must be
 * completed before one exists.
 */
export async function loginWithPassword(
  email: string,
  password: string,
  csrfToken: string,
): Promise<LoginOutcome> {
  const result = await authFetch("/login", loginResponseSchema, {
    method: "POST",
    body: JSON.stringify({ email, password }),
    csrfToken,
  });
  return "challengeName" in result
    ? { kind: "challenge", challenge: result }
    : { kind: "session", session: result };
}

/**
 * Replace a temporary password and establish the session. CSRF-gated with the
 * token the challenge carried — the same one login used, since the API reads
 * rather than rotates it until a session actually begins.
 */
export function completeNewPasswordChallenge(
  challenge: NewPasswordChallenge,
  newPassword: string,
  attributes: Record<string, string> = {},
): Promise<Session> {
  // Filter here as well as in the form: this is the boundary that talks to the
  // API, and the API answers 422 for either protected name.
  const userAttributes = Object.fromEntries(
    Object.entries(attributes).filter(([name]) => !PROTECTED_ATTRIBUTES.has(name.toLowerCase())),
  );
  return authFetch("/challenge/new-password", sessionResponseSchema, {
    method: "POST",
    body: JSON.stringify({
      username: challenge.username,
      newPassword,
      challengeSession: challenge.challengeSession,
      userAttributes,
    }),
    csrfToken: challenge.csrfToken,
  });
}

/** Mint a new access token from the refresh cookie. CSRF-gated. */
export function refreshSession(csrfToken: string): Promise<Session> {
  return authFetch("/refresh", sessionResponseSchema, { method: "POST", csrfToken });
}

/**
 * Where to send the browser to sign in with Microsoft.
 *
 * `returnTo` is how the API learns which SPA to come back to — both products
 * share one registered Cognito callback. The API validates it against its own
 * allow-list *before* the browser leaves, so an unknown origin fails at the
 * start of the flow rather than after a successful sign-in.
 */
export function microsoftStartUrl(): string {
  const returnTo = encodeURIComponent(window.location.origin);
  return `${AUTH_BASE_URL}/oauth/microsoft/start?returnTo=${returnTo}`;
}

/**
 * End the session. Returns the Hosted UI sign-out URL when one is configured.
 *
 * The API has already revoked the refresh token and cleared its cookies by the
 * time this resolves, but the IdP's own session can only be ended by a
 * top-level navigation, which is the caller's job.
 *
 * `returnTo` names this origin for the same reason `microsoftStartUrl` does, and
 * omitting it is not a neutral default: the API builds the Hosted UI
 * `logout_uri` from it, and a caller that names no origin gets the *portal's*
 * (`PORTAL_WEB_BASE_URL`), so signing out of compliance landed the user on the
 * portal's login page. The allow-list check is unchanged — an origin it does not
 * serve still falls back — so this only ever selects between origins the
 * deployment already configures.
 */
export async function logout(csrfToken: string): Promise<{ logoutUrl: string | null }> {
  const returnTo = encodeURIComponent(window.location.origin);
  const send = (token: string) =>
    fetch(`${AUTH_BASE_URL}/logout?returnTo=${returnTo}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Request-Id": crypto.randomUUID(),
        "X-CSRF-Token": token,
      },
      credentials: "include",
      signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
    });

  let response = await send(csrfToken);

  // Same recovery as authFetch above. Logout keeps its own request because its
  // response is not a Session and so cannot share that parser — but sign-out is
  // the action where an unrecoverable stale token matters most: without this,
  // every click resends the same token and the user cannot sign out at all.
  if (await isStaleCsrf(response)) {
    response = await send((await bootstrapSession()).csrfToken);
  }

  if (!response.ok) throw await authError(response);
  const body = (await response.json()) as { ok: boolean; logoutUrl: string | null };
  return { logoutUrl: body.logoutUrl ?? null };
}
